import * as React from "react";
import { forwardRef, useMemo } from "react";
import styled from "@mui/material/styles/styled";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import clsx from "clsx";
import {
  PatientFirstSummaryFragment,
  SummarySectionType,
} from "fe-common/graphql";
import { SectionAreaWithActions } from "./sectionAreaWithActions";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { KAHUN_PLATFORM } from "user-management/src/components/loginForm";
import { SectionAction } from "./types/SectionAction";

//region [[ Styles ]]

const PatientFirstSummaryViewView = styled("div")({
  marginTop: "16px",
});

const SummaryContent = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const SectionTitle = styled("div")({
  fontSize: "15px",
  marginBottom: "8px",
  color: KahunColorMap.PrimaryDim,
  fontWeight: "bold",
});

const SectionText = styled("div")({
  whiteSpace: "pre-line",
  "&.chief-complaint": {
    fontSize: "20px",
    fontWeight: "bold",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientFirstSummaryViewProps {
  id?: string;
  className?: string;
  patientSummary: PatientFirstSummaryFragment;
  showPatientInformation: boolean;
  supportedSections: string[];
  hiddenSections?: string[];
  onNotify: (text?: string) => void;
  loading?: boolean;
  clinicName: string;
  clinicId: string;
  platform: KAHUN_PLATFORM;
  disableTooltip?: boolean;
  sectionActions?: SectionAction[];
  inExtension?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientFirstSummaryView = forwardRef<
  any,
  PatientFirstSummaryViewProps
>(({ sectionActions, ...props }, ref) => {
  const { translate } = useTranslateByGender();

  const notifySectionTextCopied = (sectionName: string) => {
    props.onNotify?.(
      `${sectionName ? sectionName + " " : ""}${translate("COPIED")}`,
    );
  };

  const enabledSections = useMemo(() => {
    return props.patientSummary?.sections?.filter(
      (s) =>
        s.content !== null &&
        s.content !== "\n" &&
        (!props.supportedSections?.length ||
          props.supportedSections?.find(
            (supported) => supported === s?.type,
          )) &&
        !props.hiddenSections?.includes(s?.type),
    );
  }, [props.supportedSections, props.patientSummary]);

  const getSectionActions = (section) =>
    sectionActions?.filter?.((sa) =>
      sa.section
        ?.split?.("|")
        ?.some?.((s) => s?.trim() === section || s?.trim() === "*"),
    );

  if (!props.patientSummary) {
    return <div>{translate("SUMMARY_ERROR")}</div>;
  }

  return (
    <PatientFirstSummaryViewView
      id={props.id || "patient-summary"}
      className={clsx("patient-first-summary-view", props.className)}
      ref={ref}
    >
      <SummaryContent className={"summary-content"}>
        {props.showPatientInformation && (
          <SectionAreaWithActions
            className={"section-patient-info"}
            onCopy={(ref) =>
              notifySectionTextCopied(translate("PATIENT_INFORMATION"))
            }
            platform={props.platform}
            clinicName={props.clinicName}
            clinicId={props.clinicId}
            actions={getSectionActions(SummarySectionType.Assessment)}
            sectionName={SummarySectionType.Assessment}
            inExtension={props.inExtension}
          >
            <SectionTitle>
              {props.patientSummary.gender}, {props.patientSummary.age}
            </SectionTitle>
          </SectionAreaWithActions>
        )}
        {props.patientSummary.main && (
          <SectionAreaWithActions
            className={"section-chief-complaint"}
            onCopy={(ref) =>
              notifySectionTextCopied(translate("CHIEF_COMPLAINT"))
            }
            platform={props.platform}
            clinicName={props.clinicName}
            clinicId={props.clinicId}
            actions={getSectionActions("chief-complaint")}
            sectionName={"chief-complaint"}
            inExtension={props.inExtension}
          >
            {/*<Inline>*/}
            <SectionTitle className={"section-title"}>
              {translate("CHIEF_COMPLAINT")}
            </SectionTitle>
            <SectionText className={"section-content"}>
              {props.patientSummary.main}
            </SectionText>
            {/*</Inline>*/}
          </SectionAreaWithActions>
        )}
        {enabledSections.map((section, index, arr) => (
          <SectionAreaWithActions
            className={clsx("section-area", "kahun-" + section.type)}
            key={section.type}
            noDivider={index === arr.length - 1}
            onCopy={(ref) => notifySectionTextCopied(section.title)}
            platform={props.platform}
            clinicName={props.clinicName}
            clinicId={props.clinicId}
            actions={getSectionActions(section.type)}
            sectionName={section.type}
            inExtension={props.inExtension}
          >
            {section.title && (
              <SectionTitle className={"section-title"}>
                {section.title}
              </SectionTitle>
            )}
            <SectionText className={"section-content"}>
              {section.content}
            </SectionText>
          </SectionAreaWithActions>
        ))}
      </SummaryContent>
    </PatientFirstSummaryViewView>
  );
});
